import axios, { type AxiosInstance } from 'axios';
import { useDashboardStore } from '@/features/dashboard';
import { CSRFProtection, RateLimiter, SecurityLogger, SECURITY_CONFIG, SecureStorage } from '@/lib/security-enhancements';
import { getLoginUrl, isPublicRoute } from '@/config/routes.config';

const getDefaultBaseURL = () => {
  return import.meta.env.VITE_API_URL || 'http://localhost:3000';
};

/** Root API host (no `/api/v1` suffix). Used for `/health` and env display. */
export function getApiRoot(): string {
  const store = useDashboardStore.getState();
  return (store.currentEnvironment?.apiUrl || getDefaultBaseURL()).replace(/\/$/, '');
}

/** Axios baseURL including `/api/v1`. */
export function resolveApiBaseURL(): string {
  return `${getApiRoot()}/api/v1`;
}

export function createApiClient(): AxiosInstance {
  const apiClient = axios.create({
    baseURL: resolveApiBaseURL(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  apiClient.interceptors.request.use(
    (config) => {
      config.baseURL = resolveApiBaseURL();

      config.withCredentials = true;

      const currentStore = useDashboardStore.getState();
      const currentEnv = currentStore.currentEnvironment;
      const isStateChanging = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '');
      const enableCSRF = currentEnv?.enableCSRF !== false;
      const shouldAddCSRF = enableCSRF && isStateChanging;
      
      if (shouldAddCSRF) {
        try {
          const csrfToken = CSRFProtection.getToken();
          if (csrfToken && config.headers) {
            config.headers['X-CSRF-Token'] = csrfToken;
          } else {
            SecurityLogger.log('CSRF_TOKEN_NOT_AVAILABLE', { 
              url: config.url,
              method: config.method 
            }, 'medium');
          }
        } catch (error) {
          SecurityLogger.log('CSRF_TOKEN_EXTRACTION_FAILED', { error }, 'low');
        }
      }

      // Envelope JSON bodies as { data: ... } except Salesforce (flat body for API-key clients)
      const method = (config.method || 'get').toUpperCase();
      const url = `${config.baseURL || ''}${config.url || ''}`;
      const isSalesforce = /\/salesforce(\/|$|\?)/.test(url) || /\/salesforce(\/|$|\?)/.test(config.url || '');
      const hasJsonBody =
        config.data !== undefined &&
        config.data !== null &&
        !(typeof FormData !== 'undefined' && config.data instanceof FormData) &&
        !(typeof Blob !== 'undefined' && config.data instanceof Blob) &&
        !ArrayBuffer.isView(config.data);

      if (
        ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) &&
        hasJsonBody &&
        !isSalesforce
      ) {
        const payload = config.data;
        const alreadyEnveloped =
          typeof payload === 'object' &&
          payload !== null &&
          !Array.isArray(payload) &&
          Object.prototype.hasOwnProperty.call(payload, 'data') &&
          Object.keys(payload as object).every((k) =>
            ['data', 'meta', 'message'].includes(k),
          );
        if (!alreadyEnveloped) {
          config.data = { data: payload };
        }
      }

      const rateLimitKey = `${config.method}:${config.url}`;
      const rateLimit = SECURITY_CONFIG.API_RATE_LIMIT;
      if (!RateLimiter.checkLimit(rateLimitKey, rateLimit.maxRequests, rateLimit.windowMs)) {
        SecurityLogger.logSuspiciousActivity('RATE_LIMIT_EXCEEDED', { url: config.url, method: config.method });
        return Promise.reject(new Error('Too many requests. Please wait before trying again.'));
      }

      return config;
    },
    (error) => {
      SecurityLogger.log('REQUEST_ERROR', { error: error.message }, 'medium');
      return Promise.reject(error);
    }
  );

  apiClient.interceptors.response.use(
    (response) => {
      const csrfToken = response.headers['x-csrf-token'];
      if (csrfToken) {
        CSRFProtection.updateTokenFromHeader(csrfToken);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (originalRequest.url?.includes('/auth/login')) {
          SecurityLogger.logAuthEvent('failed_login', { reason: 'unauthorized' });
          SecureStorage.removeItem('user_profile');

          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          const refreshResponse = await apiClient.post('/auth/refresh');
          
          const newCsrfToken = refreshResponse.headers['x-csrf-token'];
          if (newCsrfToken) {
            CSRFProtection.updateTokenFromHeader(newCsrfToken);
          }

          return apiClient(originalRequest);
        } catch (refreshError: any) {
          const errorMessage = refreshError?.response?.data?.message || refreshError?.message || 'Unknown error';
          
          SecurityLogger.logAuthEvent('session_expired', { 
            reason: 'refresh_failed',
            errorMessage,
            originalUrl: originalRequest.url,
          });
          
          SecureStorage.removeItem('user_profile');
          
          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            if (!isPublicRoute(currentPath)) {
              window.location.href = getLoginUrl();
            }
          }
          
          return Promise.reject(refreshError);
        }
      } else if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message || '';
        
        if (errorMessage.includes('CSRF token') && !originalRequest._csrfRetry) {
          originalRequest._csrfRetry = true;
          
          try {
            // Health stays outside `/api/v1`
            const healthResponse = await apiClient.get('/health', { baseURL: getApiRoot() });
            const csrfToken = healthResponse.headers['x-csrf-token'];
            
            if (csrfToken) {
              CSRFProtection.updateTokenFromHeader(csrfToken);
              
              if (originalRequest.headers) {
                originalRequest.headers['X-CSRF-Token'] = csrfToken;
              }
              
              return apiClient(originalRequest);
            }
          } catch (csrfError) {
            SecurityLogger.log('CSRF_TOKEN_FETCH_FAILED', { 
              error: csrfError,
              originalUrl: originalRequest.url 
            }, 'high');
          }
        }
        
        SecurityLogger.logSuspiciousActivity('FORBIDDEN_ACCESS_ATTEMPT', {
          url: error.config?.url,
          method: error.config?.method,
          message: errorMessage,
        });
      } else if (error.response?.status === 429) {
        SecurityLogger.logSuspiciousActivity('RATE_LIMIT_EXCEEDED_SERVER', {
          url: error.config?.url,
        });
      } else if (!error.response && error.message?.includes('CORS')) {
        SecurityLogger.log('CORS_ERROR', {
          url: error.config?.url,
          method: error.config?.method,
          message: 'CORS policy blocked the request. Check backend CORS configuration.',
        }, 'high');
      }

      return Promise.reject(error);
    }
  );

  return apiClient;
}

export function getCurrentApiUrl(): string {
  return getApiRoot();
}

export function getCurrentWsUrl(): string {
  const store = useDashboardStore.getState();
  return store.currentEnvironment?.wsUrl || 
    (import.meta.env.VITE_WS_URL || 'ws://localhost:3000');
}

let apiClientInstance: AxiosInstance | null = null;

export function getApiClient(): AxiosInstance {
  if (!apiClientInstance) {
    apiClientInstance = createApiClient();
  }
  return apiClientInstance;
}
